import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { EventStatus, MatchDetailsViewModel, DisciplinesType, DisciplineLiveStatus, sportradarEventStatus, EsportsDisciplines } from '@om-fe/models';
import { AppState, seoMetadataUpdater } from '@om-fe/core/core/types';
import getConfig, { MatchPageConfig, OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { EMRoutingContextInterface, RouteNames } from '@om-fe/core/core/routing/utils';
import MarketTemplateMappingsContext from '@om-fe/core/components/market-template-mappings-context/marketTemplateMappingsContext';
import UserBehaviorTrackingContext from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking-context';
import { UserBehaviorTrackingLocation } from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking.utils';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import {
    MatchDetailsStateExt,
    SLICE_MATCH_DETAILS,
    subscribeToMatchDetails,
    unsubscribeMatchDetails,
} from '@om-fe/core/components/match-details/match-details.store';
import getSlice from '@om-fe/core/utils/get-slice';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import MarketGroups from '@om-fe/core/components/market-groups/market-groups';
import MatchDetailsDelayInfo from '@om-fe/core/components/match-details/match-details-delay-info';
import { getLiveSportsRouteParams, getLiveEventSidebarMatchListConfig } from '@om-fe/core/components/match-details/utils/match-details.utils';
import { ACTIVE_MATCH_TYPES } from '@om-fe/core/utils/match';
import RedirectTo from '@om-fe/core/components/redirect-to/redirect-to';
import LiveEventPageSkeleton from '@om-fe/core/pages/theme1/live-event-page-skeleton';
import withRouting from '@om-fe/core/core/routing/with-routing';
import { LiveEventRouteParams } from '@om-fe/core/core/routing/route-params';
import { MarketTemplateMappings } from '@om-fe/core/core/markets/types';
import { BemMethod, createBem } from '@om-fe/utils';
import { ItemWidthType } from '@om-fe/widgets/dist/components/nav/nav.constants';
import MatchDetailsHeaderLiveEventPage, { matchDetailsHeaderLiveEventPageBem } from '../../components/match-details/match-details-header-live-event-page';
import { Breadcrumbs } from '../../components/breadcrumbs/breadcrumbs';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import EventSlider from '@om-fe/core/components/event-slider/event-slider';
import { StmDataModel, stmCustomEvent } from '@om-fe/core/utils/config/sportradar-tag-manager';
import { EventMappingRpcResponseItem, getEventMapping } from '@om-fe/api';
import { WidgetType } from '@om-fe/widgets';
import MatchDetailsHeaderDefault from '@om-fe/core/components/match-details/match-details-header-default';
import MatchDetailsHeaderHighlighted from '@om-fe/core/components/match-details/match-details-header-highlighted';
import isEmpty from 'lodash/isEmpty';

interface LiveEventContentStoreProps {
    readonly breakpoints: Breakpoints;
    readonly event?: MatchDetailsViewModel | null;
    readonly sliceKey: string;
    readonly lang: string;
    readonly isLarge?: boolean;
}

interface LiveEventContentPublicProps {
    readonly id: string;
    readonly disciplineId: string;
    readonly marketTemplateMappings: MarketTemplateMappings;
    readonly onMetadataUpdate: seoMetadataUpdater;
    readonly showEventCategory?: boolean;
}

type LiveEventContentProps = LiveEventContentPublicProps
    & LiveEventContentStoreProps
    & RouteComponentProps<LiveEventRouteParams>
    & I18nContextInterface
    & EMRoutingContextInterface;

interface LiveEventContentState {
    readonly isCountdownActive: boolean;
    readonly betRadarStatistics?: EventMappingRpcResponseItem;
    readonly shouldRedirect?: boolean;
}

enum DefaultRedirect {
    DELAY = 0,
}

const bemMarketContainer: BemMethod = createBem('MarketContainer');
const instanceId: string = 'live-event-content';

const globalConfig: OMFE2ConfigModel = getConfig();
const enableMatchHeaderDefaultAndHighlighted: boolean | undefined = globalConfig.enableMatchHeaderDefaultAndHighlighted;

class LiveEventContentComponent extends React.Component<LiveEventContentProps, LiveEventContentState> {
    state: LiveEventContentState = {
        isCountdownActive: false,
        betRadarStatistics: undefined,
        shouldRedirect: false,
    };

    private redirectTimer?: number;

    async checkEventWidget(): Promise<void> {
        const { event, selectedLanguage } = this.props;
        const matchId = event.eventId;

        if (!matchId || !selectedLanguage) {
            return;
        }
        
        const eventMappingData = await getEventMapping({
            lang: selectedLanguage,
            matchId,
        });

        if (eventMappingData.STATS && !isEmpty(eventMappingData.STATS)) {
            const eventStatistics: EventMappingRpcResponseItem | undefined =
                eventMappingData.STATS.find(
                    (widget: EventMappingRpcResponseItem) =>
                        (widget.key === WidgetType.Betradar || widget.key === WidgetType.BetradarLMT)
                );
            this.setState({
                betRadarStatistics: eventStatistics,
            });
        }
    }

    async componentDidMount() {
        const { event } = this.props;
        const { isCountdownActive } = this.state;
        const matchIsOver: boolean = !ACTIVE_MATCH_TYPES.includes(event.statusId as EventStatus);

        if (enableMatchHeaderDefaultAndHighlighted) {
            await this.checkEventWidget();
        }

        if (event) {
            this.updateMetadata();
        }

        if (matchIsOver && !isCountdownActive) {
            this.setState({ shouldRedirect: true });
        }
    }

    sendSportradarViewItem() {
        const { event, lang } = this.props;
        const stmView: StmDataModel = {
            event: 'view_item',
            payload: {
                sportsEvent: {
                    match_id: event?.eventId,
                    sr_match_id: '',
                    team_home_name: event?.homeName,
                    team_home_name_short: '',
                    team_home_logo: '',
                    team_away_name: event?.awayName,
                    team_away_name_short: '',
                    team_away_logo: '',
                    scheduled: '',
                    status: event ? sportradarEventStatus?.[event.statusId] ?? '' : '',
                    tournament_name: event?.tournamentName,
                    sport: event?.disciplineName,
                    odds_home: '',
                    odds_draw: '',
                    odds_away: '',
                    language: lang,
                },
            },
        };

        if (stmCustomEvent) {
            stmCustomEvent(stmView);
        }
    }

    componentDidUpdate(prevProps: LiveEventContentProps) {
        const { id, lang, event, selectedLanguage } = this.props;
        const { id: prevId, lang: prevLang } = prevProps;
        const { isCountdownActive, shouldRedirect } = this.state;
        const { redirectDelay, statusId } = event;
        const matchIsOver: boolean = !ACTIVE_MATCH_TYPES.includes(statusId as EventStatus);

        if (event) {
            if (id !== prevId || lang !== prevLang) {
                this.updateMetadata();
            }
            if (matchIsOver && redirectDelay && !isCountdownActive && enableMatchHeaderDefaultAndHighlighted) {
                this.showCountdown();
            }
        }

        if (enableMatchHeaderDefaultAndHighlighted && (!event.eventId || !selectedLanguage)) {
            this.checkEventWidget()
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                });
        }

        if (
            !this.redirectTimer &&
            !shouldRedirect &&
            !isCountdownActive &&
            matchIsOver
        ) {
            this.redirectTimer = window.setTimeout(() => {
                this.setState({ shouldRedirect: true });
            }, event.redirectDelay ?? DefaultRedirect.DELAY);
        }
    }

    componentWillUnmount() {
        if (this.redirectTimer) {
            clearTimeout(this.redirectTimer);
            this.redirectTimer = undefined;
        }
    }

    updateMetadata() {
        const { onMetadataUpdate, event } = this.props;
        const enabledSportradar: boolean = !!globalConfig.sportradar?.clientId;

        if (!event) {
            return;
        }

        const { title, awayName, homeName, tournamentName } = event;

        onMetadataUpdate({
            title,
            keywords: `${homeName}, ${awayName}, ${tournamentName}`,
        });

        if (enabledSportradar) {
            this.sendSportradarViewItem();
        }
    }


    handleCountdownCallback = (): void => {
        const { history, getPath, event, gettext } = this.props;

        if (!event) {
            return;
        }

        const { disciplineId, disciplineName, sportParentId, sportParentName, displayChildren } = event;

        const liveSportsRouteParams = getLiveSportsRouteParams({
            disciplineId,
            disciplineName,
            sportParentId,
            sportParentName,
            displayChildren,
            allLocationText: gettext('All'),
        });

        history.push(getPath(RouteNames.liveSports, liveSportsRouteParams));
    };

    getRedirectNode(): React.ReactNode {
        const { event, gettext } = this.props;
        const { shouldRedirect } = this.state;

        if (!event) {
            return <RedirectTo routeName={RouteNames.home} />;
        }

        if (!shouldRedirect) {
            return null;
        }

        const { disciplineId, disciplineName, sportParentId, sportParentName, displayChildren } = event;
        const liveSportsRouteParams = getLiveSportsRouteParams({
            disciplineId,
            disciplineName,
            sportParentId,
            sportParentName,
            displayChildren,
            allLocationText: gettext('All'),
        });

        return (
            <RedirectTo
                routeName={RouteNames.liveSports}
                routeParams={liveSportsRouteParams}
            />
        );
    }

    getBreadcrumbsNode(): React.ReactNode {
        const { match: { params: { id } }, event, isLarge, showEventCategory } = this.props;
        const {
            disciplineId,
            locationId,
            parentId: tournamentId,
            categoryId,
            sportParentName,
            sportParentId,
        } = event;

        if (sportParentId === DisciplinesType.ElectronicSports || sportParentId === DisciplinesType.Fighting) {
            return (
                <Breadcrumbs
                    disciplineId={disciplineId}
                    locationId={locationId}
                    categoryId={categoryId}
                    tournamentId={tournamentId}
                    isLarge={isLarge}
                    isLive={true}
                    eventId={id}
                    routeType={DisciplineRouteType.Location}
                />
            );
        }

        return (
            <Breadcrumbs
                disciplineId={sportParentId || disciplineId}
                locationId={locationId}
                tournamentId={tournamentId}
                sportChildId={sportParentId && disciplineId}
                isLarge={isLarge}
                isLive={true}
                eventId={id}
                categoryId={categoryId}
                routeType={sportParentName
                    ? DisciplineRouteType.Parent
                    : (showEventCategory ? DisciplineRouteType.Category : DisciplineRouteType.Location)}
            />
        );
    }

    showCountdown(): void {
        this.setState({
            isCountdownActive: true,
        });
    }

    render() {
        const {
            id,
            marketTemplateMappings,
            event,
            breakpoints,
            disciplineId,
            lang,
            isLarge,
            sliceKey,
        } = this.props;

        const {
            isCountdownActive,
            betRadarStatistics,
        } = this.state;

        const redirectNode: React.ReactNode = this.getRedirectNode();

        if (redirectNode) {
            return redirectNode;
        }
        const { numberOfEventsSlider } = getLiveEventSidebarMatchListConfig();
        const matchPageConfig: MatchPageConfig | undefined = globalConfig.matchPageConfig;
        const showEventSlider: boolean | undefined = breakpoints.small || breakpoints.medium;
        const enableEventsSlider: boolean = globalConfig?.enableEventsSlider;

        const showHighlightedHeader: boolean = event.sportParentId === DisciplinesType.ElectronicSports
            && event.disciplineId !== EsportsDisciplines.ETennis
            && event.disciplineId !== EsportsDisciplines.EVolleyball;

        return (
            <>
                {
                    isLarge && (
                        this.getBreadcrumbsNode()
                    )
                }
                {enableMatchHeaderDefaultAndHighlighted
                    ?
                        showHighlightedHeader
                            ?
                                <div className={matchDetailsHeaderLiveEventPageBem('MatchHeroArea', { [`Sport-${disciplineId}`]: !!disciplineId, 'HighlightedHeader': true })}>
                                    <MatchDetailsHeaderHighlighted
                                        sliceKey={sliceKey}
                                        matchId={id}
                                        showScore={true}
                                        countdownCallback={this.handleCountdownCallback}
                                        displayCountdown={isCountdownActive}
                                        instanceId="highlighted-header"
                                        betRadarStatistics={betRadarStatistics}
                                    />
                                </div>
                            :
                                <div className={matchDetailsHeaderLiveEventPageBem('MatchHeroArea')}>
                                    <MatchDetailsHeaderDefault
                                        sliceKey={sliceKey}
                                        matchId={id}
                                        showScore={true}
                                        countdownCallback={this.handleCountdownCallback}
                                        displayCountdown={isCountdownActive}
                                        instanceId="classic-header"
                                        betRadarStatistics={betRadarStatistics}
                                    />
                                </div>
                    :
                        <MatchDetailsHeaderLiveEventPage
                            disciplineId={disciplineId}
                            matchId={id}
                            lang={lang}
                            breakpoints={breakpoints}
                        />
                }
                {enableEventsSlider && showEventSlider && id && disciplineId && (
                    <EventSlider
                        liveStatus={DisciplineLiveStatus.LIVE}
                        limitNumberOfEvents={numberOfEventsSlider}
                        routeName={RouteNames.liveEvent}
                        disciplineId={disciplineId}
                        instanceId={instanceId}
                        eventId={id}
                    />
                )}
                <div className={bemMarketContainer()}>
                    {
                        event && id && (
                            <>
                                <MarketTemplateMappingsContext.Provider value={marketTemplateMappings}>
                                    <UserBehaviorTrackingContext.Provider value={{ userBehaviorTrackingPage: RouteNames.event, userBehaviorTrackingLocation: UserBehaviorTrackingLocation.MatchDetails }}>
                                        <MarketGroups
                                            matchId={id}
                                            showMarketGroupsHeader={true}
                                            itemWidthType={ItemWidthType.ItemToStartUnequalWidth}
                                            instanceId={`${instanceId}-${id}`}
                                        />
                                    </UserBehaviorTrackingContext.Provider>
                                </MarketTemplateMappingsContext.Provider>
                            </>
                        )
                    }
                    {
                        matchPageConfig
                        && matchPageConfig.displayDelayInfo
                        && event
                        && event.isLive
                        && (
                            <MatchDetailsDelayInfo />
                        )
                    }
                </div>
            </>
        );
    }
}

const LiveEventContent: React.ComponentType<LiveEventContentPublicProps> = compose(
    withTranslation<LiveEventContentPublicProps>(),
    withRouting<LiveEventContentPublicProps>(),
    withRouter,
    preLoader<LiveEventContentProps>({
        preload({ props, dispatch }) {
            const { id, isAuthenForTopic } = props;
            
            dispatch(subscribeToMatchDetails(id, isAuthenForTopic));
        },
        update({ prevProps, props, dispatch }) {
            const { id, isAuthenForTopic } = props;
            const { id: prevId, isAuthenForTopic: prevIsAuthenForTopic } = prevProps;

            if (id && prevId) {
                if (id !== prevId || isAuthenForTopic !== prevIsAuthenForTopic) {
                    dispatch(unsubscribeMatchDetails(prevId, prevIsAuthenForTopic));
                    dispatch(subscribeToMatchDetails(id, isAuthenForTopic));
                }
            }
        },
        getReadySlice({ }: LiveEventContentProps): string {
            return `${SLICE_MATCH_DETAILS}.ready`;
        },
        destroy: ({ props, dispatch }) => {
            const { id, isAuthenForTopic } = props;

            if (id) {
                dispatch(unsubscribeMatchDetails(id, isAuthenForTopic));
            }
        },
        loadingAnimation: LiveEventPageSkeleton,
    }),
    connect<LiveEventContentStoreProps, null, LiveEventContentProps, AppState>(
        (state: AppState): LiveEventContentStoreProps => {
            const slice: MatchDetailsStateExt | undefined = getSlice<MatchDetailsStateExt>(state, SLICE_MATCH_DETAILS);

            if (!slice) {
                return {
                    breakpoints: state.breakpoints,
                    lang: state.config.lang,
                    sliceKey: SLICE_MATCH_DETAILS,
                };
            }

            return {
                breakpoints: state.breakpoints,
                event: slice.event,
                lang: state.config.lang,
                sliceKey: SLICE_MATCH_DETAILS,
                isLarge: (state.breakpoints.large || state.breakpoints.xl),
            };
        },
    ),
)(LiveEventContentComponent);
export default LiveEventContent;
